// src/components/weeklyReport/SubmittedReportsPanel.js
//
// Admin Reports → "Submitted reports": the patient-level weekly reports as a
// grid, 9 per page, newest first (ReportsGrid). Follows the page's time-period
// filter (a patient list shares its id with its weekly_reports doc, so the
// page passes the ids it is currently showing) and has its own coordinator
// filter (All, or one coordinator).
// Admin-only page; Firestore rules also limit these lists to admins.

import { useEffect, useMemo, useState } from "react";
import { loadAllReportPatients } from "../../services/weeklyReportService";
import ReportsGrid from "./ReportsGrid";
import { INPUT } from "./ui";

export { PAGE_SIZE } from "./ReportsGrid";

const SubmittedReportsPanel = ({ visibleReportIds }) => {
  const [state, setState] = useState({ loading: true, error: "", reports: [] });
  const [coordinator, setCoordinator] = useState("All");

  useEffect(() => {
    let cancelled = false;
    loadAllReportPatients()
      .then((reports) => !cancelled && setState({ loading: false, error: "", reports }))
      .catch((err) => {
        console.error("❌ Could not load submitted reports:", err);
        if (!cancelled) setState({ loading: false, error: "Submitted reports couldn't be loaded.", reports: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const inPeriod = useMemo(
    () => state.reports.filter((r) => !visibleReportIds || visibleReportIds.has(r.id)),
    [state.reports, visibleReportIds]
  );
  const names = useMemo(
    () => [...new Set(inPeriod.map((r) => r.coordinator || "Unknown"))].sort((a, b) => a.localeCompare(b)),
    [inPeriod]
  );
  const shown = useMemo(
    () => inPeriod.filter((r) => coordinator === "All" || (r.coordinator || "Unknown") === coordinator),
    [inPeriod, coordinator]
  );

  if (state.loading) return <p className="text-sm text-slate-400 text-center py-4">Loading submitted reports…</p>;
  if (state.error) return <p className="text-sm text-rose-300 text-center py-4">{state.error}</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={coordinator}
          onChange={(e) => setCoordinator(e.target.value)}
          className={`${INPUT} max-w-[14rem] py-1.5`}
          aria-label="Filter by coordinator"
        >
          <option value="All" className="bg-[#0b0820]">
            All coordinators
          </option>
          {names.map((n) => (
            <option key={n} value={n} className="bg-[#0b0820]">
              {n}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-slate-400">
          {shown.length} {shown.length === 1 ? "report" : "reports"} in this period
        </span>
      </div>
      <ReportsGrid
        reports={shown}
        label="Submitted reports"
        emptyText="No patient-level reports in this period. Reports from before the new form only have numbers (see the coordinator cards below)."
      />
    </div>
  );
};

export default SubmittedReportsPanel;
